
//@ts-nocheck
import { Essence } from '@repo/types';
import { Badge } from '@repo/ui/components/ui/badge';
import { Button } from '@repo/ui/components/ui/button';
import { Card, CardContent, CardHeader } from '@repo/ui/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/components/ui/tabs';
import { services } from '@repo/ui/lib/api/services/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, TreePine, X, Info, Leaf, Sparkles, CheckCircle2, Circle } from 'lucide-react';
import * as React from "react";
import { useState } from 'react';
import { EssenceForm } from './essence-form';
import { Alert, AlertDescription } from '@repo/ui/components/ui/alert';
import { cn } from '../../lib/utils';

interface EssenceTabsProps {
  readonly epaysageId: number;
  readonly onEssenceChange?: (essences: Essence[]) => void;
}

export function EssenceTabs({ epaysageId, onEssenceChange }: EssenceTabsProps) {
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [isCreating, setIsCreating] = useState(false);
  const queryClient = useQueryClient();

  // Fetch essences for this epaysage
  const { data: essencesResponse, isLoading } = useQuery({
    queryKey: ['essences', epaysageId],
    queryFn: () => services.essence.getEssences(epaysageId, 1, 100),
    enabled: !!epaysageId,
  });

  const essences = essencesResponse?.data?.sort((a, b) => a.id - b.id) || [];
  const MAX_ESSENCES = 5;
  const canAddEssence = essences.length < MAX_ESSENCES;

  // Delete essence mutation
  const deleteEssence = useMutation({
    mutationFn: (essenceId: number) =>
      services.essence.deleteEssence(epaysageId, essenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['essences', epaysageId] });
      // If we deleted the active essence, switch to overview
      if (activeTab !== 'overview' && activeTab !== 'new-essence') {
        const deletedEssenceId = Number.parseInt(activeTab.replace('essence-', ''), 10);
        if (essences.some(e => e.id === deletedEssenceId)) {
          setActiveTab('overview');
        }
      }
    },
  });

  const handleCreateEssence = () => {
    if (!canAddEssence) {
      return;
    }
    setIsCreating(true);
    setActiveTab('new-essence');
  };

  const handleEssenceCreated = (newEssence: Essence) => {
    queryClient.invalidateQueries({ queryKey: ['essences', epaysageId] });
    setIsCreating(false);
    setActiveTab(`essence-${newEssence.id}`);

    if (onEssenceChange) {
      onEssenceChange([...essences, newEssence]);
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setActiveTab('overview');
  };

  const handleDeleteEssence = (essenceId: number) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette essence ?')) {
      deleteEssence.mutate(essenceId);
    }
  };

  React.useEffect(() => {
    if (onEssenceChange) {
      onEssenceChange(essences);
    }
  }, [essences, onEssenceChange]);

  if (isLoading) {
    return (
      <Card className="border-0 shadow-lg bg-gradient-to-br from-card via-card to-primary/5 backdrop-blur-sm overflow-hidden">
        <CardContent className="p-12">
          <div className="flex flex-col items-center justify-center gap-6">
            <div className="relative">
              <div className="h-16 w-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <TreePine className="h-7 w-7 text-primary animate-pulse" />
              </div>
              <div className="absolute -inset-2 bg-primary/10 rounded-full animate-ping" />
            </div>
            <div className="text-center space-y-2">
              <span className="text-foreground font-semibold text-lg block">Chargement des essences...</span>
              <span className="text-muted-foreground text-sm">Préparation de vos données</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  console.log('essences', essences);

  return (
    <Card className="w-full border-0 shadow-xl bg-gradient-to-br from-card via-card to-primary/5 backdrop-blur-sm overflow-hidden transition-all duration-300 hover:shadow-2xl">

      <CardHeader className="relative bg-gradient-to-br from-primary/8 via-primary/5 to-primary/2 border-b border-primary/20 pb-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/15 via-primary/5 to-transparent" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

        <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 ring-2 ring-primary/30 shadow-lg transition-all duration-300 hover:scale-110 hover:ring-primary/50">
              <TreePine className="h-7 w-7 text-primary drop-shadow-sm" />
              <div className="absolute -inset-1 bg-primary/20 rounded-2xl blur-sm opacity-50 animate-pulse" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground tracking-tight flex items-center gap-2">
                Gestion des essences
                {essences.length > 0 && (
                  <Sparkles className="h-5 w-5 text-primary animate-pulse" />
                )}
              </h2>
              <div className="flex items-center gap-2 mt-1.5">
                <p className="text-sm font-medium text-muted-foreground">
                  <span className="text-primary font-semibold">{essences.length}</span> / {MAX_ESSENCES} essences configurées
                </p>
                {essences.length === MAX_ESSENCES && (
                  <Badge variant="secondary" className="ml-2 bg-primary/10 text-primary border-primary/20">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Complet
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {!canAddEssence && (
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200/50 shadow-sm">
                <Info className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">
                  Maximum atteint
                </span>
              </div>
            )}
            <Button
              onClick={handleCreateEssence}
              disabled={isCreating || !canAddEssence}
              className="flex-1 sm:flex-none gap-2 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <Plus className="h-4 w-4" />
              Ajouter une essence
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="inline-flex h-auto p-1.5 bg-gradient-to-r from-muted/50 to-muted/30 rounded-2xl gap-2 flex-wrap border border-border/50 shadow-inner">
            <TabsTrigger
              value="overview"
              className={cn(
                "flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all duration-300",
                "data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/90 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105",
                "data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-muted/80"
              )}
            >
              <Leaf className="h-4 w-4" />
              Vue d'ensemble
              {essences.length > 0 && (
                <Badge variant="secondary" className="ml-1.5 bg-primary-foreground/20 text-primary-foreground border-0 font-bold px-2 py-0.5">
                  {essences.length}
                </Badge>
              )}
            </TabsTrigger>

            {essences.map((essence, index) => (
              <TabsTrigger
                key={essence.id}
                value={`essence-${essence.id}`}
                className={cn(
                  "flex items-center gap-2 max-w-56 px-4 py-2.5 rounded-xl font-medium transition-all duration-300 group",
                  "data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/90 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105",
                  "data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-muted/80"
                )}
              >
                <span className="truncate flex-1">
                  Essence {index + 1} - {essence.espece?.genre} {essence.espece?.name}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 rounded-md  group-hover:opacity-100 transition-opacity hover:bg-destructive/20 hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (essence.id) {
                      handleDeleteEssence(essence.id);
                    }
                  }}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </TabsTrigger>
            ))}

            {isCreating && (
              <TabsTrigger
                value="new-essence"
                className={cn(
                  "flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium bg-gradient-to-r from-primary/20 to-primary/10 text-primary border-2 border-primary/30 shadow-sm "
                )}
              >
                <Plus className="h-4 w-4" />
                Nouvelle essence
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview" className="mt-8 animate-in fade-in-50 duration-300">
            <EssenceOverview
              essences={essences}
              onSelectEssence={(essenceId) => setActiveTab(`essence-${essenceId}`)}
              onCreateEssence={handleCreateEssence}
              canAddEssence={canAddEssence}
              maxEssences={MAX_ESSENCES}
            />
          </TabsContent>

          {essences.map((essence, index) => (
            <TabsContent key={essence.id} value={`essence-${essence.id}`} className="mt-8 animate-in fade-in-50 duration-300">
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-primary/5 to-primary/2 border border-primary/10">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-primary/10 ring-2 ring-primary/20">
                    <TreePine className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">Essence {index + 1}</h3>
                    <p className="text-sm font-medium text-muted-foreground mt-0.5">
                      {essence.espece?.genre} {essence.espece?.name}
                    </p>
                  </div>
                </div>
              </div>
              <EssenceForm
                epaysageId={epaysageId}
                essence={essence}
                mode="edit"
                onSuccess={() => {
                  queryClient.invalidateQueries({ queryKey: ['essences', epaysageId] });
                }}
                onCancel={() => setActiveTab('overview')}
              />
            </TabsContent>
          ))}

          {isCreating && (
            <TabsContent value="new-essence" className="mt-8 animate-in fade-in-50 duration-300">
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border-2 border-primary/20 border-dashed">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-primary/20 ring-2 ring-primary/30 animate-pulse">
                    <Plus className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">Essence {essences.length + 1}</h3>
                    <p className="text-sm font-medium text-muted-foreground mt-0.5">
                      Ajoutez une nouvelle essence à cet espace boisé
                    </p>
                  </div>
                </div>
              </div>
              <EssenceForm
                epaysageId={epaysageId}
                mode="create"
                //@ts-ignore
                onSuccess={handleEssenceCreated}
                onCancel={handleCancelCreate}
              />
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
}

interface EssenceOverviewProps {
  readonly essences: Essence[];
  readonly onSelectEssence: (essenceId: number) => void;
  readonly onCreateEssence: () => void;
  readonly canAddEssence: boolean;
  readonly maxEssences: number;
}

function EssenceOverview({ essences, onSelectEssence, onCreateEssence, canAddEssence, maxEssences }: EssenceOverviewProps) {
  if (essences.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <div className="relative inline-block mb-6">
          <div className="absolute inset-0 bg-primary/20 rounded-full blur-2xl animate-pulse" />
          <div className="relative flex items-center justify-center h-24 w-24 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 ring-4 ring-primary/10">
            <TreePine className="h-12 w-12 text-primary" />
          </div>
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-3">
          Aucune essence définie
        </h3>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto text-base">
          Commencez par ajouter des essences à cet espace boisé. Vous pouvez configurer jusqu'à <span className="font-semibold text-primary">{maxEssences} essences</span>.
        </p>
        <Button
          onClick={onCreateEssence}
          disabled={!canAddEssence}
          className="flex items-center gap-2 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 px-6 py-6 text-base"
          size="lg"
        >
          <Plus className="h-5 w-5" />
          Ajouter la première essence
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!canAddEssence && (
        <Alert className="border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 shadow-sm">
          <Info className="h-5 w-5 text-amber-600" />
          <AlertDescription className="text-sm font-medium text-amber-800">
            Vous avez atteint le maximum de <span className="font-bold">{maxEssences} essences</span> autorisées pour cet espace boisé.
          </AlertDescription>
        </Alert>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {essences.map((essence, index) => (
          <Card
            key={essence.id}
            className={cn(
              "cursor-pointer group relative overflow-hidden transition-all duration-300",
              "hover:shadow-xl hover:scale-[1.02] hover:border-primary/30",
              "bg-gradient-to-br from-card via-card to-primary/5",
              "border-2 border-border/50"
            )}
            onClick={() => onSelectEssence(essence.id ?? 0)}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/0 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <CardContent className="p-5 relative">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all duration-300 group-hover:scale-110">
                    <TreePine className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-foreground text-base mb-1 group-hover:text-primary transition-colors">
                      Essence {index + 1}
                    </h4>
                    <p className="text-sm font-medium text-muted-foreground truncate">
                      {essence.espece?.genre} {essence.espece?.name}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-border/50">
                <Badge
                  variant={essence.status_travaux ? 'default' : 'secondary'}
                  className={cn(
                    "font-semibold",
                    essence.status_travaux
                      ? "bg-gradient-to-r from-primary to-primary/90 text-primary-foreground shadow-sm"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {essence.status_travaux ? (
                    <>
                      <CheckCircle2 className="h-3 w-3 mr-1.5" />
                      Actif
                    </>
                  ) : (
                    <>
                      <Circle className="h-3 w-3 mr-1.5" />
                      Inactif
                    </>
                  )}
                </Badge>
                <div className="text-xs text-muted-foreground font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Cliquer pour éditer →
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
